import { DynamoDBClient, GetItemCommand, UpdateItemCommand } from '@aws-sdk/client-dynamodb';
import { marshall, unmarshall } from '@aws-sdk/util-dynamodb';
import { ENVIRONMENT_VARIABLES } from 'src/constants/EnvironmentConstants';
import { CognitoService } from 'src/services/aws/CognitoService';

const stage = ENVIRONMENT_VARIABLES.env.Stage;
const normalizedStage = stage.toLowerCase() === 'dev' ? 'alpha' : stage.toLowerCase();
const TABLE_NAME = `finsuite_ea_huddle_employee_settings_${normalizedStage}`;

export interface LeaderEAConfig {
  leaderAlias: string;
  eaEmailAddress: string | null;
  groupType: string | null;
  groupId: string | null;
  overrideMembers?: string[];
  lastUpdatedBy: string;
  lastUpdatedAt: string;
}

/**
 * Service for managing leader EA email configuration in DynamoDB.
 * Stores all configs in a single record as a map keyed by leader alias.
 *
 * Schema:
 * - pk: GLOBAL_CONFIG
 * - sk: LEADER_EA_CONFIGS
 * - configs: { [leaderAlias]: LeaderEAConfig }
 *
 * Note: Single-item approach is bounded by DDB's 400KB item limit.
 * At ~300 bytes per entry, this supports ~1000+ leaders comfortably.
 */
export class LeaderEAConfigService {
  private static client: DynamoDBClient | null = null;
  private static cache: LeaderEAConfig[] | null = null;
  private static cacheExpiry = 0;
  private static readonly CACHE_TTL_MS = 5 * 60 * 1000;

  private static getClient(): DynamoDBClient {
    if (this.client) return this.client;
    this.client = new DynamoDBClient({
      region: ENVIRONMENT_VARIABLES.env.Region,
      credentials: CognitoService.credentialProvider
    });
    return this.client;
  }

  /**
   * Get all leader EA configs from DDB. Cached in memory for 5 minutes.
   */
  public static async getAll(skipCache = false): Promise<LeaderEAConfig[]> {
    if (!skipCache && this.cache && Date.now() < this.cacheExpiry) {
      return this.cache;
    }

    const client = this.getClient();
    const result = await client.send(
      new GetItemCommand({
        TableName: TABLE_NAME,
        Key: marshall({
          employee_alias: 'GLOBAL_CONFIG',
          sk: 'LEADER_EA_CONFIGS'
        })
      })
    );

    if (!result.Item) {
      this.cache = [];
      this.cacheExpiry = Date.now() + this.CACHE_TTL_MS;
      return [];
    }

    const item = unmarshall(result.Item);
    const configsMap = (item.configs ?? {}) as Record<string, Omit<LeaderEAConfig, 'leaderAlias'>>;
    const configs = Object.entries(configsMap).map(([alias, config]) => ({
      leaderAlias: alias,
      ...config
    })) as LeaderEAConfig[];

    this.cache = configs;
    this.cacheExpiry = Date.now() + this.CACHE_TTL_MS;
    return configs;
  }

  /**
   * Get all leader EA configs synchronously from cache.
   * Returns empty array if cache is not yet populated.
   * Call preload() on app init to ensure cache is warm.
   */
  public static getAllCached(): LeaderEAConfig[] {
    return this.cache ?? [];
  }

  /**
   * Pre-load the cache. Call on app init so synchronous reads work.
   * Respects TTL — only fetches if cache is cold or expired.
   */
  public static async preload(): Promise<void> {
    await this.getAll();
  }

  /**
   * Find a leader EA config by alias synchronously from cache.
   */
  public static findByAliasCached(leaderAlias: string): LeaderEAConfig | undefined {
    return this.getAllCached().find((c) => c.leaderAlias.toLowerCase() === leaderAlias.toLowerCase());
  }

  /**
   * Find a leader EA config by alias (async, ensures loaded).
   */
  public static async findByAlias(leaderAlias: string): Promise<LeaderEAConfig | undefined> {
    const all = await this.getAll();
    return all.find((c) => c.leaderAlias.toLowerCase() === leaderAlias.toLowerCase());
  }

  /**
   * Save (create or update) a leader EA config.
   * Uses atomic SET on the map key — no read required.
   * Prerequisite: The GLOBAL_CONFIG/LEADER_EA_CONFIGS item with a configs map must exist in DDB.
   */
  public static async save(config: LeaderEAConfig): Promise<void> {
    const client = this.getClient();
    const { leaderAlias, ...rest } = config;
    const key = leaderAlias.toLowerCase();

    await client.send(
      new UpdateItemCommand({
        TableName: TABLE_NAME,
        Key: marshall({
          employee_alias: 'GLOBAL_CONFIG',
          sk: 'LEADER_EA_CONFIGS'
        }),
        UpdateExpression: 'SET configs.#alias = :config, lastUpdatedAt = :now',
        ExpressionAttributeNames: { '#alias': key },
        ExpressionAttributeValues: marshall({ ':config': rest, ':now': new Date().toISOString() }, { removeUndefinedValues: true })
      })
    );
    this.invalidateCache();
  }

  /**
   * Delete a leader EA config by alias.
   * Uses atomic REMOVE on the map key — no read required.
   */
  public static async delete(leaderAlias: string): Promise<void> {
    const client = this.getClient();
    const key = leaderAlias.toLowerCase();

    await client.send(
      new UpdateItemCommand({
        TableName: TABLE_NAME,
        Key: marshall({
          employee_alias: 'GLOBAL_CONFIG',
          sk: 'LEADER_EA_CONFIGS'
        }),
        UpdateExpression: 'SET lastUpdatedAt = :now REMOVE configs.#alias',
        ExpressionAttributeNames: { '#alias': key },
        ExpressionAttributeValues: marshall({ ':now': new Date().toISOString() })
      })
    );
    this.invalidateCache();
  }

  /** Invalidate the in-memory cache. */
  public static invalidateCache(): void {
    this.cache = null;
    this.cacheExpiry = 0;
  }
}
